import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, getToken, money } from '../api.js';
import { ReportHead, Money, Empty } from '../components/Shell.jsx';
import { chainToken } from '../lib/chainToken.js';
import { ackKey } from '../lib/ackKey.js';

const STATUS_LABEL = {
  captured: 'Captured — not yet read',
  extracted: 'Read — awaiting review',
  needs_review: 'Needs review',
  extraction_failed: 'Could not be read',
  posted: 'Posted',
  rejected: 'Rejected',
};

// The queue is what needs a human. Posted and rejected slips are done, and
// are shown only when "All slips" is picked.
const QUEUE_STATUSES = ['captured', 'extracted', 'needs_review', 'extraction_failed'];

/**
 * Is this displayed amount zero?
 *
 * A string test, deliberately — nothing in this browser turns a money string
 * into a number, not even to compare it with nought. The server holds cents as
 * BigInt and sends "16.63"; Number("16.63") is where a float creeps back in.
 */
const isZeroAmount = (v) => v === null || v === undefined || /^-?0*(\.0*)?$/.test(String(v).trim());

/**
 * Full-size slip, zoomable and pannable.
 *
 * A faded thermal till slip is unreadable at panel width, and checking a VAT
 * number or a total against what the model read is the whole job of this
 * screen. Wheel or the buttons to zoom, drag to pan, double-click to reset,
 * Esc to close.
 *
 * NO NEW DEPENDENCY. A CSS transform does all of it, and this project has
 * consistently declined to add a library for something this size — the same
 * call as base64 uploads over a multipart parser.
 *
 * IT DOES NOT FETCH. It is handed the blob URL useSlipImage already made, so
 * there is one object URL per slip-page and one owner of its lifetime. The
 * caller renders this only while that URL exists, so the overlay cannot
 * outlive the URL it is showing — see the `imgUrl && zoomed` guard at the
 * call site. That guard covers a PAGE change exactly as it already covered a
 * slip change: useSlipImage revokes the old object URL and clears `url`
 * before the new page's fetch begins, so there is no tick where this overlay
 * could hold a URL that has already been revoked.
 *
 * The pager props mirror the panel's own pager exactly — including
 * `showPager`/`atLastPage`, which are computed once at the call site so the
 * overlay and the panel can never disagree about whether there is a next
 * page or how many pages there are. A reviewer zoomed in to check a total
 * therefore does not have to close the overlay to move to the next page.
 * `onNext` is only ever called when there is a next page — see `atLastPage` —
 * and never wraps back to page 1, which is exactly how a reviewer would
 * misread which page they are looking at. A `null` `pageCount` means the
 * count is unknown, and renders as "of ?" rather than as a made-up number.
 */
function SlipImageViewer({ url, alt, onClose, pageNo, pageCount, showPager, atLastPage, onPrev, onNext }) {
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const stage = useRef(null);
  const panel = useRef(null);
  const drag = useRef(null);
  // Mirrored in state as well as the ref: the ref is what the move handler
  // reads (it must not trigger a render per pointermove), and the state is
  // what the cursor is drawn from. A ref alone changed no cursor at all,
  // because mutating one renders nothing.
  const [grabbing, setGrabbing] = useState(false);

  const clamp = (v) => Math.min(8, Math.max(0.25, v));
  const reset = useCallback(() => { setScale(1); setPan({ x: 0, y: 0 }); }, []);

  // Non-passive, so preventDefault actually stops the page scrolling behind
  // the overlay. React's own onWheel is registered passive and cannot.
  useEffect(() => {
    const el = stage.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      setScale((s) => clamp(s * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Esc closes; Tab cycles within the overlay rather than escaping to the page
  // behind it. Cheap because everything focusable in here is a button.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusable = panel.current?.querySelectorAll('button');
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Focus moves in on open and back to whatever opened it on close, so a
  // keyboard user is never dropped at the top of the document.
  useEffect(() => {
    const restoreTo = document.activeElement;
    panel.current?.querySelector('button')?.focus();
    return () => { if (restoreTo instanceof HTMLElement) restoreTo.focus(); };
  }, []);

  const startDrag = (e) => {
    drag.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    setGrabbing(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onDrag = (e) => {
    if (!drag.current) return;
    setPan({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const endDrag = (e) => {
    drag.current = null;
    setGrabbing(false);
    if (e.currentTarget.hasPointerCapture?.(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <div ref={panel} role="dialog" aria-modal="true" aria-label={alt}
         style={{
           position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(16,18,20,0.92)',
           display: 'flex', flexDirection: 'column',
         }}>
      <div style={{
        display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end',
        padding: '10px 14px', flex: '0 0 auto',
      }}>
        <span style={{ marginRight: 'auto', color: '#cfd4d9', fontSize: 13 }}>
          Scroll or use −/+ to zoom · drag to move · double-click to reset · Esc to close
        </span>
        {showPager && (
          <>
            <button className="icon-btn" onClick={onPrev} disabled={pageNo <= 1} aria-label="Previous page">
              ‹
            </button>
            <span style={{ color: '#cfd4d9', fontSize: 13, minWidth: 88, textAlign: 'center' }}>
              Page {pageNo} of {pageCount ?? '?'}
            </span>
            <button className="icon-btn" onClick={onNext} disabled={atLastPage} aria-label="Next page">
              ›
            </button>
          </>
        )}
        <button className="icon-btn" onClick={() => setScale((s) => clamp(s / 1.25))}
                aria-label="Zoom out">−</button>
        <button className="icon-btn" onClick={reset} aria-label="Reset zoom"
                style={{ minWidth: 62 }}>{Math.round(scale * 100)}%</button>
        <button className="icon-btn" onClick={() => setScale((s) => clamp(s * 1.25))}
                aria-label="Zoom in">+</button>
        <button className="btn btn--quiet" onClick={onClose}>Close</button>
      </div>

      <div ref={stage}
           onDoubleClick={reset}
           onPointerDown={startDrag} onPointerMove={onDrag}
           onPointerUp={endDrag} onPointerCancel={endDrag}
           style={{
             flex: '1 1 auto', overflow: 'hidden', display: 'grid', placeItems: 'center',
             cursor: grabbing ? 'grabbing' : 'grab', touchAction: 'none',
           }}>
        <img src={url} alt={alt} draggable={false}
             style={{
               transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
               transformOrigin: 'center center',
               maxWidth: '92vw', maxHeight: '82vh',
               userSelect: 'none', willChange: 'transform',
             }} />
      </div>
    </div>
  );
}

/**
 * Fetch one page of a slip's image as a blob URL. A plain <img src> cannot
 * carry the bearer token.
 *
 * `pageNo` is in the effect's own dependency array, so paging reuses exactly
 * the same lifetime machinery a slip change already relies on: the previous
 * page's object URL is revoked before the new page's fetch even starts, and
 * the `cancelled` guard means a slow page-2 response landing after the
 * reviewer has already moved to page 3 (or to a different slip) cannot
 * overwrite what is on screen with a stale URL.
 */
function useSlipImage(slipId, pageNo) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setUrl(null); setErr(null);
    if (!slipId) return undefined;
    let cancelled = false;
    let objectUrl = null;

    fetch(`/api/slips/${slipId}/image?page=${pageNo ?? 1}`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then(async (res) => {
        if (!res.ok) {
          const payload = await res.json().catch(() => null);
          throw new Error(payload?.error ?? `could not load the image (${res.status})`);
        }
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((e) => { if (!cancelled) setErr(e.message); });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [slipId, pageNo]);

  return { url, err };
}

/**
 * The review queue: a bookkeeper works each slip — checks the image against
 * what was read off it, sees why it is waiting and whether its input VAT is
 * claimable, then allocates and posts (or rejects) it.
 *
 * The s.20 verdict and `reviewReason` are surfaced exactly as the extraction
 * left them — they are already written for a bookkeeper, not re-worded here.
 * The proposed allocation comes from `GET /api/slips/:id/proposal`, fetched
 * lazily on selection (one call per slip looked at, not per row) — that
 * route runs the SAME matchRule/proposeFromHistory the worker runs, so the
 * "Rule" vs "History suggestion" badge can never disagree with what would
 * actually fire. (An earlier version of this screen re-derived that
 * client-side; it drifted from the real matching in ways that mattered —
 * see the route's own comment in api/src/routes/slips.js.)
 *
 * This screen assumes `slips.review` and `slips.rules` always travel
 * together, which is true of every role in api/src/lib/capabilities.js
 * today — a reviewer who can post a slip can also confirm a rule for it.
 * If a future role ever splits them, the rule-creation offer below needs
 * its own capability check.
 */
export function SlipReview({ entity }) {
  const [slips, setSlips] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [taxCodes, setTaxCodes] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [proposal, setProposal] = useState(null);
  const [proposalLoading, setProposalLoading] = useState(false);
  const [accountId, setAccountId] = useState('');
  const [taxCodeId, setTaxCodeId] = useState('');
  // The rules screen refuses this combination outright — see
  // AllocationRules.jsx's own deniedAccount/deniedMismatch. Here it cannot be
  // refused (a restaurant or caterer's OWN entertainment supplies are
  // genuinely claimable, and only a human reviewing this one slip knows
  // which case it is), so it is a warning that must be explicitly
  // acknowledged before Post is enabled — see `deniedMismatch` below.
  //
  // Stored as the (slip, account, tax code) combination it was GIVEN for
  // — see lib/ackKey.js — never as a plain boolean reset by an effect. A
  // boolean reset in a `useEffect([accountId, taxCodeId])` looked
  // equivalent and was not: React bails out of an effect whose deps are
  // referentially equal to last render's, so when two CONSECUTIVE slips
  // proposed the SAME account+code pair — the Zappas-history case this
  // feature exists for — the effect never re-fired and a tick from the
  // PREVIOUS slip silently authorised the next post. Keying the
  // acknowledgement to the exact combination removes that failure mode
  // structurally: there is no reset to forget to run.
  const [ackedFor, setAckedFor] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  // What the reviewer is correcting. Amounts stay STRINGS exactly as typed and
  // are sent that way; the route converts them with toCents().
  const [form, setForm] = useState(null);
  const [ruleOffer, setRuleOffer] = useState(null);
  const [err, setErr] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const canRules = (entity?.capabilities ?? []).includes('slips.rules');

  const loadSlips = useCallback(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/slips`)
      .then((d) => setSlips(d.slips)).catch((e) => setErr(e.message));
  }, [entity?.id]);

  useEffect(() => {
    if (!entity?.id) return;
    loadSlips();
    Promise.all([
      api.get(`/api/entities/${entity.id}/accounts`),
      api.get(`/api/entities/${entity.id}/tax-codes`),
    ]).then(([a, t]) => {
      setAccounts(a.accounts.filter((x) => x.is_postable));
      setTaxCodes(t.taxCodes.filter((x) => x.direction !== 'output'));
    }).catch(() => {});
  }, [entity?.id]); // eslint-disable-line

  const queue = useMemo(() => {
    if (!slips) return [];
    return showAll ? slips : slips.filter((s) => QUEUE_STATUSES.includes(s.status));
  }, [slips, showAll]);

  const selected = useMemo(() => slips?.find((s) => s.id === selectedId) ?? null, [slips, selectedId]);
  // `?? null`, NOT `?? 1`. routes/slips.js goes to deliberate trouble to send
  // `pageCount: null` — rather than 0 — when a query did not join the count,
  // on the grounds that 0 "would be a false statement, not a missing one".
  // Collapsing that to 1 makes it a false statement again, and a worse one:
  // for a three-page slip it hides the pager entirely, so a reviewer checks a
  // total against page 1 believing they have seen the whole slip. Unknown
  // stays unknown, and says so on screen as "of ?".
  const pageCount = selected?.pageCount ?? null;
  // Show the pager when there is more than one page — or when we do not know
  // how many there are. A visible question mark beats an invisible wrong
  // answer.
  const showPager = pageCount === null || pageCount > 1;

  // Which page of the CURRENT slip is on screen. Reset to 1 whenever the
  // selected slip changes — a page number from a five-page slip must never
  // carry over onto a slip that only has two, and starting anywhere but
  // page 1 on a freshly opened slip is its own way to show the wrong page.
  const [pageNo, setPageNo] = useState(1);
  useEffect(() => { setPageNo(1); }, [selected?.id]);

  // With an unknown count there is no next page we can name, so the forward
  // arrow is dead: the "of ?" label is the message, not the navigation.
  const atLastPage = pageCount === null || pageNo >= pageCount;
  const nextPage = () => setPageNo((p) => (pageCount === null ? p : Math.min(pageCount, p + 1)));

  const { url: imgUrl, err: imgErr } = useSlipImage(selected?.id ?? null, pageNo);

  // The full-size viewer. Closed whenever the slip changes, so it cannot
  // reappear over the NEXT slip's image once that one finishes loading —
  // useSlipImage revokes the old object URL on the same change.
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => { setZoomed(false); }, [selected?.id]);

  // Used only for the "starting SPAR" preview text on the rule-creation
  // offer below — display, not matching. The real match happens server-side.
  const chain = selected ? chainToken(selected.supplierNameNorm) : '';

  // The proposal fetch below is async, so it can land AFTER a bookkeeper has
  // already picked an account by hand while it was still in flight. `touched`
  // tracks whether that has happened for the CURRENT slip; the seeding effect
  // below must check it before writing to the fields. A form that silently
  // overwrites a choice someone just made is worse than one that seeds
  // nothing — do not "simplify" this back to an unconditional seed.
  const touched = useRef(false);

  // Reset touched-ness for the newly selected slip. This must run — and be
  // DECLARED — before the proposal-fetch effect below, so that a stale
  // `touched.current = true` from the previous slip can never suppress the
  // new slip's first seed.
  useEffect(() => { touched.current = false; }, [selected?.id]);

  useEffect(() => {
    if (!selected) { setProposal(null); setProposalLoading(false); return undefined; }
    let cancelled = false;
    setProposalLoading(true);
    api.get(`/api/slips/${selected.id}/proposal`)
      .then((p) => { if (!cancelled) setProposal(p); })
      .catch(() => { if (!cancelled) setProposal({ source: null, accountId: null, taxCodeId: null, basis: null }); })
      .finally(() => { if (!cancelled) setProposalLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.id]);

  // Seeds the account/tax code fields from whatever this supplier's
  // evidence supports: the server's proposal (a rule, or a history guess)
  // first, whatever it was already allocated to (reviewing something
  // already posted) second, and otherwise nothing — the bookkeeper must
  // choose. Depending on `proposal` too means the fields update once the
  // lazy fetch above resolves, rather than staying seeded from nothing —
  // but ONLY when the bookkeeper has not already chosen something for this
  // slip themselves (`touched.current`); the proposal must never clobber a
  // human's own pick just because it arrived late.
  useEffect(() => {
    if (!selected) { setAccountId(''); setTaxCodeId(''); return; }
    if (touched.current) return;
    setAccountId(proposal?.accountId ?? selected.accountId ?? '');
    setTaxCodeId(proposal?.taxCodeId ?? selected.taxCodeId ?? '');
    setRejectReason('');
  }, [selected?.id, proposal]); // eslint-disable-line

  // Seed the correction fields from the slip. Keyed on the slip id alone: a
  // reviewer half-way through retyping a total must not have it replaced
  // underneath them by a background reload of the same slip.
  useEffect(() => {
    if (!selected) { setForm(null); return; }
    setForm({
      supplierName: selected.supplierName ?? '',
      slipDate: selected.slipDate ?? '',
      net: selected.net ?? '',
      vat: selected.vat ?? '',
      gross: selected.gross ?? '',
    });
  }, [selected?.id]); // eslint-disable-line

  const accountLabel = (id) => {
    const a = accounts.find((x) => x.id === id);
    return a ? `${a.code} — ${a.name}` : id;
  };
  const taxLabel = (id) => {
    const t = taxCodes.find((x) => x.id === id);
    return t ? `${t.code} — ${t.name}` : null;
  };

  // A slip showing VAT must carry a VAT code, or the VAT201 never sees the
  // claim. See the note beside the Post button.
  const needsTaxCode = Boolean(selected) && !isZeroAmount(selected.vat) && !taxCodeId;

  /**
   * The gap the rules screen already closes for an unattended rule, open
   * here on the one path that can actually post today: `input_denied` and
   * `is_input_denied` come off the same accounts/tax-codes endpoints
   * AllocationRules.jsx reads (see its own deniedAccount/deniedMismatch) —
   * this screen keeps no second copy of which accounts s.17(2) denies.
   *
   * postSlip is deliberately NOT made to refuse this: a restaurant or
   * caterer whose business IS entertainment supplies these as taxable
   * supplies and may legitimately claim the input tax, so a blanket refusal
   * would be wrong for every hospitality client. A rule posts unattended
   * forever and must be safe by default; a manual post is one human decision
   * made once with the warning in front of them — so this warns, and
   * requires an explicit acknowledgement, rather than blocking.
   */
  const chosenAccount = useMemo(() => accounts.find((a) => a.id === accountId) ?? null, [accounts, accountId]);
  const chosenTaxCode = useMemo(() => taxCodes.find((t) => t.id === taxCodeId) ?? null, [taxCodes, taxCodeId]);
  const deniedMismatch = Boolean(chosenAccount?.input_denied) && Boolean(taxCodeId) && !chosenTaxCode?.is_input_denied
    ? chosenAccount
    : null;

  // Does the ticked box cover exactly THIS (slip, account, tax code)? No
  // reset effect needed — see the note on `ackedFor` above. It can only
  // ever match the one combination it was given for.
  const acknowledged = ackedFor === ackKey(selected?.id, accountId, taxCodeId);

  /**
   * Correct what the model misread — or key in a slip it never read at all.
   *
   * With no LLM_API_KEY configured every slip arrives here with no figures on
   * it, so this is the ONLY way such a slip becomes postable. Amounts go up as
   * the strings they were typed as; `PATCH /api/slips/:id` converts them with
   * toCents() and refuses anything malformed with the message shown here.
   */
  const saveCorrections = async () => {
    if (!selected || !form) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.patch(`/api/slips/${selected.id}`, {
        supplierName: form.supplierName.trim() || null,
        slipDate: form.slipDate || null,
        net: form.net.trim() || null,
        vat: form.vat.trim() || null,
        gross: form.gross.trim() || null,
      });
      setMsg('Corrections saved.');
      setForm({
        supplierName: r.slip.supplierName ?? '', slipDate: r.slip.slipDate ?? '',
        net: r.slip.net ?? '', vat: r.slip.vat ?? '', gross: r.slip.gross ?? '',
      });
      loadSlips();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const post = async () => {
    if (!selected || !accountId || needsTaxCode || (deniedMismatch && !acknowledged)) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      const r = await api.post(`/api/slips/${selected.id}/post`, {
        accountId, taxCodeId: taxCodeId || undefined,
      });
      setMsg(`Posted as entry ${r.entryNo}.`);
      // The engine learns from a CONFIRMED decision, never from a guess: only
      // offer to create a rule when this allocation did not already come from
      // one. Re-confirming what a rule already said teaches it nothing new.
      setRuleOffer(proposal?.source !== 'rule' && canRules
        ? { chain, accountId, taxCodeId: taxCodeId || null, account: accountLabel(accountId) }
        : null);
      setSelectedId(null);
      loadSlips();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!selected) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/api/slips/${selected.id}/reject`, { reason: rejectReason || undefined });
      setMsg('Slip rejected.');
      setSelectedId(null);
      loadSlips();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const retry = async () => {
    if (!selected) return;
    setBusy(true); setErr(null); setMsg(null);
    try {
      await api.post(`/api/slips/${selected.id}/retry-extraction`);
      // The route QUEUES; it does not read. An extraction takes over a minute
      // and a default proxy gives up at sixty seconds, so waiting for the
      // result in the request would have meant this button appearing to fail
      // on almost every press. Say what actually happened instead of implying
      // a result is on its way back.
      setMsg('Queued to be read again. The reader picks it up within about 15 seconds — '
             + 'refresh in a moment to see the result.');
      loadSlips();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const confirmRule = async () => {
    if (!ruleOffer) return;
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/entities/${entity.id}/allocation-rules`, {
        supplierNorm: ruleOffer.chain, accountId: ruleOffer.accountId,
        taxCodeId: ruleOffer.taxCodeId ?? undefined,
      });
      setMsg(`Rule created. Future slips from ${ruleOffer.chain} will allocate to ${ruleOffer.account} automatically.`);
      setRuleOffer(null);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  if (!slips) return <div className="empty">Loading…</div>;

  return (
    <>
      <ReportHead entity={entity} title="Review queue"
                  sub="Check each slip against its image, see why it is here, and allocate it." />

      {err && <div className="error">{err}</div>}
      {msg && <div className="ok">{msg}</div>}

      {ruleOffer && (
        <div className="note note--warn">
          <strong>Always allocate {ruleOffer.chain} to {ruleOffer.account}?</strong>
          <div style={{ margin: '6px 0' }}>
            This will apply to slips from any supplier starting <strong>{ruleOffer.chain}</strong>.
          </div>
          <button className="btn btn--quiet" onClick={confirmRule} disabled={busy} style={{ marginRight: 8 }}>
            Create the rule
          </button>
          <button className="icon-btn" onClick={() => setRuleOffer(null)}>No thanks</button>
        </div>
      )}

      <div className="controls">
        <label className="field"><span>Show</span>
          <select value={showAll ? 'all' : 'queue'} onChange={(e) => setShowAll(e.target.value === 'all')}>
            <option value="queue">Needs attention</option>
            <option value="all">All slips</option>
          </select></label>
      </div>

      {queue.length === 0 ? (
        <Empty><p>Nothing waiting for review.</p></Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Date</th>
                <th>Supplier</th>
                <th className="num" style={{ width: 110 }}>Total</th>
                <th style={{ width: 170 }}>Status</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {queue.map((s) => (
                <tr key={s.id} aria-current={s.id === selectedId ? 'true' : undefined}
                    style={{ cursor: 'pointer', background: s.id === selectedId ? 'var(--paper-raised)' : undefined }}
                    onClick={() => setSelectedId(s.id)}>
                  <td className="figure">{s.slipDate ?? '—'}</td>
                  <td>
                    {s.supplierName ?? '—'}
                    {s.compliance && s.compliance.claimable === false && (
                      <span className="neg" style={{ fontSize: 11 }}> · VAT not claimable yet</span>
                    )}
                  </td>
                  <td className="num"><Money value={s.gross} /></td>
                  <td>{STATUS_LABEL[s.status] ?? s.status}</td>
                  <td style={{ fontSize: 12, color: 'var(--ink-soft)' }}>{s.reviewReason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'minmax(220px, 360px) 1fr', gap: 24,
          marginTop: 26, paddingTop: 22, borderTop: '1px solid var(--rule)',
        }}>
          <div>
            {imgErr && <div className="error">{imgErr}</div>}
            {imgUrl
              ? (
                <>
                  {/* A button, not an onClick on the img: a faded thermal slip
                      is unreadable at this width, and reaching the full-size
                      view must not require a mouse. */}
                  <button type="button" onClick={() => setZoomed(true)}
                          title="Click to view full size"
                          style={{
                            display: 'block', padding: 0, border: 0, background: 'none',
                            cursor: 'zoom-in', width: '100%',
                          }}>
                    <img src={imgUrl} alt={`Slip from ${selected.supplierName ?? 'unknown supplier'}`}
                         style={{ maxWidth: '100%', border: '1px solid var(--rule-strong)' }} />
                  </button>
                  <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-soft)' }}>
                    Click the slip to enlarge.
                  </div>
                </>
              )
              : !imgErr && <div className="empty">Loading image…</div>}
            {showPager && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <button className="icon-btn" onClick={() => setPageNo((p) => Math.max(1, p - 1))}
                        disabled={pageNo <= 1} aria-label="Previous page">
                  ‹
                </button>
                <span style={{ fontSize: 12, color: 'var(--ink-soft)' }}>
                  Page {pageNo} of {pageCount ?? '?'}
                </span>
                {/* Disabled on the last page rather than wrapping — wrapping
                    back to page 1 is exactly how a reviewer would end up
                    checking a total against the wrong page while believing
                    they are still on the last one. */}
                <button className="icon-btn" onClick={nextPage}
                        disabled={atLastPage} aria-label="Next page">
                  ›
                </button>
              </div>
            )}
          </div>

          <div>
            <h2 className="eyebrow" style={{ margin: '0 0 10px' }}>
              {selected.supplierName ?? 'Unknown supplier'} · {money(selected.gross)}
            </h2>

            {/* "Could not be read" and "needs review" looked identical on
                this screen, and a slip the worker has GIVEN UP on looked
                identical to one still waiting its turn. The reviewer could
                not tell whether to wait or to act. */}
            {selected.workerGaveUp && selected.status !== 'posted' && (
              <div className="note note--warn">
                {/* The count comes from the server, not from a literal here:
                    MAX_ATTEMPTS lives in slipWorker.js and this sentence would
                    quietly stop being true if it changed. */}
                <strong>Nothing more will happen on its own.</strong> The reader tried{' '}
                {selected.workerAttempts} times and gave up
                {selected.reviewReason ? '' : ' without saying why'}. Use “Read it again”
                below to put it back in the queue.
              </div>
            )}
            {!selected.workerGaveUp && selected.queued && selected.status !== 'posted' && (
              <div className="note">
                Queued for reading — this usually takes a minute or two.
              </div>
            )}

            {selected.reviewReason && (
              <div className="note note--warn">
                <strong>Why this is here:</strong> {selected.reviewReason}
              </div>
            )}

            {selected.status === 'rejected' && (
              <div className="note">
                This slip was rejected and its entry reversed. Posting it now creates a new entry;
                the reversal stays on the record.
              </div>
            )}

            {selected.compliance && selected.compliance.claimable === false && (
              <div className="error">
                <strong>This slip's input VAT is not yet claimable — call the client while it is fresh.</strong>
                <ul style={{ margin: '8px 0 0 18px', padding: 0 }}>
                  {selected.compliance.missing.map((m) => (
                    <li key={m.field} style={{ marginBottom: 6 }}>
                      {m.field} <span style={{ opacity: 0.75 }}>({m.why}) — {m.fix}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {selected.compliance?.notes?.length > 0 && (
              <div className="note">{selected.compliance.notes.join(' ')}</div>
            )}

            {/*
              EDITABLE, not a read-out. A slip lands here precisely because
              something about it was wrong or unreadable — most often "the
              total, VAT and net do not add up, so the figures were probably
              misread". A reviewer who can only look at the misread figures
              cannot do the job the queue exists for. And with no LLM_API_KEY
              there is no extraction at all, so these fields are the only way a
              slip ever becomes postable.

              Typed exactly as printed on the slip; the server converts. Leave
              VAT at 0 for a purchase with none.
            */}
            {form && selected.status !== 'posted' ? (
              <>
                <div className="form-grid" style={{ marginTop: 16 }}>
                  <label className="field"><span>Supplier</span>
                    <input value={form.supplierName}
                           onChange={(e) => setForm({ ...form, supplierName: e.target.value })} /></label>
                  <label className="field"><span>Date</span>
                    <input type="date" value={form.slipDate}
                           onChange={(e) => setForm({ ...form, slipDate: e.target.value })} /></label>
                  <div className="field"><span>Document no.</span>
                    <span className="figure">{selected.documentNumber ?? '—'}</span></div>
                  <label className="field"><span>Net</span>
                    <input className="input--figure" inputMode="decimal" placeholder="0.00" value={form.net}
                           onChange={(e) => setForm({ ...form, net: e.target.value })} /></label>
                  <label className="field"><span>VAT</span>
                    <input className="input--figure" inputMode="decimal" placeholder="0.00" value={form.vat}
                           onChange={(e) => setForm({ ...form, vat: e.target.value })} /></label>
                  <label className="field"><span>Total</span>
                    <input className="input--figure" inputMode="decimal" placeholder="0.00" value={form.gross}
                           onChange={(e) => setForm({ ...form, gross: e.target.value })} /></label>
                </div>
                <button className="btn btn--quiet" style={{ marginTop: 10 }}
                        onClick={saveCorrections} disabled={busy}>
                  Save corrections
                </button>
              </>
            ) : (
              <div className="form-grid" style={{ marginTop: 16 }}>
                <div className="field"><span>Date</span><span className="figure">{selected.slipDate ?? '—'}</span></div>
                <div className="field"><span>Document no.</span><span className="figure">{selected.documentNumber ?? '—'}</span></div>
                <div className="field"><span>Net</span><span className="figure"><Money value={selected.net} /></span></div>
                <div className="field"><span>VAT</span><span className="figure"><Money value={selected.vat} /></span></div>
                <div className="field"><span>Total</span><span className="figure"><Money value={selected.gross} /></span></div>
              </div>
            )}

            {proposalLoading ? (
              <div className="note">Checking for a matching rule…</div>
            ) : proposal?.source === 'rule' ? (
              <div className="ok">
                <strong>Rule:</strong> {accountLabel(proposal.accountId)}
                {taxLabel(proposal.taxCodeId) ? ` · ${taxLabel(proposal.taxCodeId)}` : ''}
              </div>
            ) : proposal?.source === 'history' ? (
              <div className="note note--warn">
                <strong>History suggestion</strong> ({proposal.basis}) — not a confirmed rule:{' '}
                {accountLabel(proposal.accountId)}
                {taxLabel(proposal.taxCodeId) ? ` · ${taxLabel(proposal.taxCodeId)}` : ''}
              </div>
            ) : (
              <div className="note">No suggestion for this supplier yet — choose an account below.</div>
            )}

            {touched.current && proposal?.source
              && (proposal.accountId !== accountId || (proposal.taxCodeId ?? '') !== taxCodeId) && (
              <div className="note">You have chosen a different account from the suggestion above.</div>
            )}

            <div className="form-grid" style={{ marginTop: 14 }}>
              <label className="field"><span>Account</span>
                <select value={accountId}
                        onChange={(e) => { touched.current = true; setAccountId(e.target.value); }}>
                  <option value="">Choose an account</option>
                  {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                </select></label>
              <label className="field"><span>VAT</span>
                <select value={taxCodeId}
                        onChange={(e) => { touched.current = true; setTaxCodeId(e.target.value); }}>
                  <option value="">No VAT</option>
                  {taxCodes.map((t) => <option key={t.id} value={t.id}>{t.code} — {t.name}</option>)}
                </select></label>
            </div>

            {/*
              A VAT-bearing slip with no VAT code posts a VAT input line with
              tax_code_id NULL. report_vat LEFT JOINs on the tax code, so that
              line is INVISIBLE to it: the balance sheet carries input VAT the
              VAT201 knows nothing about, and the trial balance still balances,
              so nothing looks wrong anywhere. The server refuses it outright;
              this stops a reviewer being offered the button at all. "No VAT" is
              expressed by setting the VAT amount to zero above, not by leaving
              the code blank.
            */}
            {needsTaxCode && (
              <div className="note note--warn">
                This slip shows VAT of {money(selected.vat)}, so it needs a VAT code before it can be
                posted. If there is genuinely no VAT on it, set the VAT to 0.00 above and save.
              </div>
            )}

            {/*
              s.17(2) denies the input tax on this account by default, but not
              always: a restaurant or caterer whose OWN business is
              entertainment supplies these as taxable supplies and may
              genuinely claim it back. So this never blocks the way the rules
              screen blocks an unattended rule — it warns, once, in front of
              the one human making this one decision, and requires them to say
              they mean it before Post is enabled.
            */}
            {deniedMismatch && (
              <div className="error">
                <strong>
                  {deniedMismatch.code} {deniedMismatch.name} is an account where s.17(2) denies the
                  input tax. {chosenTaxCode?.code} claims it back.
                </strong>
                <div style={{ margin: '6px 0' }}>
                  Post anyway only if this client supplies entertainment (or the relevant denied
                  category) as its own taxable supply — otherwise choose{' '}
                  {deniedMismatch.default_tax_code ?? 'a denied VAT code'} instead.
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={acknowledged}
                         onChange={(e) => setAckedFor(
                           e.target.checked ? ackKey(selected?.id, accountId, taxCodeId) : null,
                         )} />
                  <span>This client genuinely supplies entertainment as its own taxable supply — claim the input tax.</span>
                </label>
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <button className="btn"
                      disabled={!accountId || needsTaxCode || busy || selected.status === 'posted'
                        || (deniedMismatch && !acknowledged)}
                      onClick={post}>
                {busy ? 'Posting…' : 'Post'}
              </button>
              <button className="btn btn--quiet"
                      disabled={busy || selected.status === 'posted'}
                      onClick={retry}>
                {busy ? 'Queueing…' : 'Read it again'}
              </button>
              <input value={rejectReason} placeholder="Reason (optional)"
                     onChange={(e) => setRejectReason(e.target.value)} style={{ width: 200 }} />
              <button className="icon-btn" disabled={busy || selected.status === 'rejected'} onClick={reject}>
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
      {zoomed && imgUrl && (
        <SlipImageViewer
          url={imgUrl}
          alt={`Slip from ${selected?.supplierName ?? 'unknown supplier'}`}
          onClose={() => setZoomed(false)}
          pageNo={pageNo}
          pageCount={pageCount}
          showPager={showPager}
          atLastPage={atLastPage}
          onPrev={() => setPageNo((p) => Math.max(1, p - 1))}
          onNext={nextPage} />
      )}
    </>
  );
}
